/**
 * Shared architect-hours payment primitives.
 *
 * Extracted from PurchaseHoursModal so the engage-an-expert flow can take payment inline,
 * on the same screen as the request, instead of bouncing the user to a separate modal.
 * Owns the Stripe sequence only — callers own their own layout, copy and success handling.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'next-themes';
import { useAuthToken } from '@/contexts/AuthContext';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { StripeCardElementOptions, StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { isValidPersonName, isValidPlaceName, isValidAddressLine } from '@/utils/billingFieldValidation';
import {
    validatePostalCode, getPostalCodePlaceholder, requiresPostalCode,
} from '@/utils/postalCodeValidation';
import { useArchitectHoursPreview } from '@/hooks/useArchitectHoursPreview';
import countryList from 'country-list';
import { Info } from 'lucide-react';
import type { CustomerType } from '@/components/billing/TaxAndBusinessFields';
import type { WalletBillingDetails } from '@/components/stripe/WalletPayButton';

// Stripe renders CardElement in a cross-origin iframe, so Tailwind dark: classes can't reach it —
// the only way to theme it is passing literal colors keyed off the live resolved theme.
export const getCardElementOptions = (isDark: boolean): StripeCardElementOptions => ({
    style: {
        base: {
            fontSize: '16px',
            color: isDark ? '#f5f2ed' : '#1e293b',
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            '::placeholder': {
                color: isDark ? '#8b93a8' : '#9ca3af',
            },
            iconColor: isDark ? '#8b93a8' : '#9ca3af',
        },
        invalid: {
            color: isDark ? '#f87171' : '#ef4444',
            iconColor: isDark ? '#f87171' : '#ef4444',
        },
    },
    // Postal code is collected via its own country-aware field below instead — Stripe's
    // built-in one has no country selector, so it can't validate non-US formats.
    hidePostalCode: true,
});

/** Thrown for bad input before any charge is attempted, so callers can title it differently. */
export class HoursPaymentValidationError extends Error {}

/** Either a linear hour block, or a fixed-price service whose price the server resolves. */
export type HoursPaymentTarget = { hours: number } | { sku: string };

export interface HoursPaymentResult {
    hours_purchased: number;
    cost?: number;
    new_balance?: number;
    payment_intent_id?: string;
}

/**
 * Drives the card-to-credited-hours sequence: address -> tax preview -> payment method ->
 * intent -> Stripe confirmation -> backend confirm. Resolves with the credited hours; throws
 * otherwise. `previewTarget` is whatever the caller currently has selected (an hour count or a
 * fixed-price SKU) — passed in up front so the tax preview can fire once the address step is
 * submitted, before pay() itself is ever called.
 */
export function useHoursPayment(previewTarget: HoursPaymentTarget | null = null) {
    const token = useAuthToken();
    const stripe = useStripe();
    const elements = useElements();

    const [cardholderName, setCardholderName] = useState('');
    const [cardComplete, setCardComplete] = useState(false);
    const [cardError, setCardError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);
    const [processingStep, setProcessingStep] = useState('');

    // Full billing address, needed for Stripe Tax (VAT) calculation — not just AVS anymore.
    const [addressLine1, setAddressLine1] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [billingCountry, setBillingCountry] = useState('US');
    const [postalCode, setPostalCode] = useState('');
    const [detectingCountry, setDetectingCountry] = useState(false);

    // Two-step flow: collect + submit the billing address first (so tax/VAT can be resolved),
    // only then show card details — same pattern as CreditPurchaseModal's checkoutStep.
    const [addressStep, setAddressStep] = useState<'address' | 'payment'>('address');

    const [appliedVoucherCode, setAppliedVoucherCode] = useState<string | null>(null);

    // Business purchase — mirrors BillingPayment.tsx's subscription checkout fields exactly.
    const [customerType, setCustomerType] = useState<CustomerType>('individual');
    const [businessName, setBusinessName] = useState('');
    const [taxIdType, setTaxIdType] = useState('');
    const [taxIdValue, setTaxIdValue] = useState('');

    const countries = useMemo(
        () => countryList.getData().sort((a, b) => a.name.localeCompare(b.name)),
        [],
    );

    useEffect(() => {
        let cancelled = false;
        setDetectingCountry(true);
        fetch('https://ipapi.co/json/')
            .then((res) => res.json())
            .then((data) => {
                if (cancelled || !data.country_code) return;
                if (countries.some((c) => c.code === data.country_code)) {
                    setBillingCountry(data.country_code);
                }
            })
            .catch(() => { /* keep the US default */ })
            .finally(() => { if (!cancelled) setDetectingCountry(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const postalValid = !requiresPostalCode(billingCountry) || validatePostalCode(postalCode, billingCountry);
    const addressValid = isValidAddressLine(addressLine1) && isValidPlaceName(city) && postalValid;

    const submitAddress = useCallback(() => {
        if (addressValid) setAddressStep('payment');
    }, [addressValid]);

    const goBackToAddress = useCallback(() => setAddressStep('address'), []);

    const previewAddress = useMemo(
        () => ({ line1: addressLine1, city, state, postal_code: postalCode, country: billingCountry }),
        [addressLine1, city, state, postalCode, billingCountry],
    );
    const walletBillingDetails = useMemo<WalletBillingDetails>(() => ({
        name: cardholderName,
        address: {
            line1: addressLine1,
            city,
            state: state || undefined,
            country: billingCountry,
            postal_code: requiresPostalCode(billingCountry) ? postalCode : undefined,
        },
    }), [cardholderName, addressLine1, city, state, billingCountry, postalCode]);

    const { preview } = useArchitectHoursPreview({
        token,
        target: previewTarget,
        address: previewAddress,
        // Enabled as soon as the address is valid (not gated to the payment step) so tax/VAT and
        // any applied voucher can be previewed while still entering the address, before card details.
        enabled: addressValid,
        voucherCode: appliedVoucherCode,
        customerType,
        businessName,
        taxIdType,
        taxIdValue,
    });
    const resolvingPreview = addressValid && preview === null;

    const handleCardChange = useCallback((event: StripeCardElementChangeEvent) => {
        setCardComplete(event.complete);
        setCardError(event.error ? event.error.message : null);
    }, []);

    const resetCard = useCallback(() => {
        setCardholderName('');
        setCardComplete(false);
        setCardError(null);
        setProcessingStep('');
        setAddressLine1('');
        setCity('');
        setState('');
        setPostalCode('');
        setAddressStep('address');
        setAppliedVoucherCode(null);
        setCustomerType('individual');
        setBusinessName('');
        setTaxIdType('');
        setTaxIdValue('');
    }, []);

    // walletPaymentMethodId comes from WalletPayButton (Apple/Google Pay) and replaces the CardElement step only.
    const pay = useCallback(async (target: HoursPaymentTarget, walletPaymentMethodId?: string): Promise<HoursPaymentResult> => {
        if (!stripe || !elements || !token) {
            throw new HoursPaymentValidationError('Payment system not initialized');
        }
        if (!walletPaymentMethodId && !cardComplete) {
            throw new HoursPaymentValidationError('Please complete your card details');
        }
        if (!walletPaymentMethodId && !isValidPersonName(cardholderName)) {
            throw new HoursPaymentValidationError('Please enter a valid cardholder name');
        }
        if (!postalValid) {
            throw new HoursPaymentValidationError('Please enter a valid postal code');
        }
        if (!addressValid) {
            throw new HoursPaymentValidationError('Please enter a valid billing address');
        }

        setProcessing(true);
        try {
            let paymentMethodId = walletPaymentMethodId;
            if (!paymentMethodId) {
                const cardElement = elements.getElement(CardElement);
                if (!cardElement) {
                    throw new Error('Card element not found');
                }

                setProcessingStep('Creating payment method...');
                const { error: methodError, paymentMethod } = await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardElement,
                    billing_details: {
                        name: cardholderName,
                        address: {
                            line1: addressLine1,
                            city,
                            state: state || undefined,
                            country: billingCountry,
                            postal_code: requiresPostalCode(billingCountry) ? postalCode : undefined,
                        },
                    },
                });
                if (methodError) {
                    throw new Error(methodError.message || 'Failed to create payment method');
                }
                if (!paymentMethod) {
                    throw new Error('No payment method returned');
                }
                paymentMethodId = paymentMethod.id;
            }

            // Send either hours or the SKU; the server prices a SKU from its own list.
            const intentBody: Record<string, unknown> = { payment_method_id: paymentMethodId };
            if ('sku' in target) intentBody.service_sku = target.sku;
            else intentBody.hours = target.hours;
            if (appliedVoucherCode) intentBody.voucher_code = appliedVoucherCode;
            intentBody.customer_type = customerType;
            if (customerType === 'business') {
                intentBody.business_name = businessName;
                intentBody.tax_id_type = taxIdType;
                intentBody.tax_id_value = taxIdValue;
            }

            setProcessingStep('Processing payment...');
            const intentResponse = await fetch(
                `${import.meta.env.VITE_BASE_API_URL}/billing/architect-hours/payment-intent`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(intentBody),
                }
            );
            if (!intentResponse.ok) {
                const errorData = await intentResponse.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to create payment intent');
            }
            const intentData = await intentResponse.json();

            // A voucher/discount left nothing for Stripe to charge (below its minimum
            // chargeable amount) — the server already granted the hours and there is no
            // PaymentIntent to confirm.
            if (intentData.requires_payment === false) {
                return {
                    hours_purchased: intentData.hours,
                    cost: intentData.amount,
                    new_balance: intentData.new_balance,
                } as HoursPaymentResult;
            }

            setProcessingStep('Confirming payment...');
            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
                intentData.client_secret
            );
            if (confirmError) {
                throw new Error(confirmError.message || 'Payment confirmation failed');
            }
            if (!paymentIntent || paymentIntent.status !== 'succeeded') {
                throw new Error(`Payment failed with status: ${paymentIntent?.status || 'unknown'}`);
            }

            setProcessingStep('Finalizing purchase...');
            const confirmResponse = await fetch(
                `${import.meta.env.VITE_BASE_API_URL}/billing/architect-hours/confirm`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        payment_intent_id: paymentIntent.id,
                        save_payment_method: false,
                        payment_method_nickname: null,
                    }),
                }
            );
            if (!confirmResponse.ok) {
                const errorData = await confirmResponse.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Failed to confirm purchase');
            }

            return await confirmResponse.json() as HoursPaymentResult;
        } finally {
            setProcessing(false);
            setProcessingStep('');
        }
    }, [
        stripe, elements, token, cardComplete, cardholderName, postalValid, addressValid,
        addressLine1, city, state, billingCountry, postalCode, appliedVoucherCode,
        customerType, businessName, taxIdType, taxIdValue,
    ]);

    return {
        cardholderName, setCardholderName,
        cardComplete, cardError, handleCardChange,
        addressLine1, setAddressLine1, city, setCity, state, setState,
        billingCountry, setBillingCountry, postalCode, setPostalCode, countries, detectingCountry,
        addressStep, addressValid, submitAddress, goBackToAddress,
        preview, resolvingPreview, walletBillingDetails,
        processing, processingStep,
        ready: !!stripe && cardComplete && !!cardholderName.trim() && postalValid,
        resetCard,
        pay,
        appliedVoucherCode, setAppliedVoucherCode,
        customerType, setCustomerType, businessName, setBusinessName,
        taxIdType, setTaxIdType, taxIdValue, setTaxIdValue,
    };
}

interface CardFieldsProps {
    cardholderName: string;
    setCardholderName: (v: string) => void;
    cardError: string | null;
    handleCardChange: (event: StripeCardElementChangeEvent) => void;
    processing: boolean;
    compact?: boolean;
}

/** Cardholder name + card element only — shown after the billing address step (below) has
 * been submitted and tax/VAT resolved, styled to match the engage dialog when compact. */
export const HoursPaymentFields: React.FC<CardFieldsProps> = ({
    cardholderName, setCardholderName, cardError, handleCardChange,
    processing, compact,
}) => {
    const { resolvedTheme } = useTheme();
    const cardOptions = useMemo(() => getCardElementOptions(resolvedTheme === 'dark'), [resolvedTheme]);

    return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
        <div>
            <label className={compact
                ? 'block text-xs font-semibold text-foreground mb-1.5'
                : 'block text-xs font-bold text-muted-foreground mb-2 uppercase tracking-wide'}>
                Cardholder name
            </label>
            <input
                type="text"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                disabled={processing}
                className={compact
                    ? 'w-full px-3.5 py-2.5 bg-white dark:bg-white/5 border border-line rounded-xl text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-plum/40 focus:ring-1 focus:ring-ring/40 disabled:opacity-50'
                    : 'w-full px-4 py-3 bg-white dark:bg-white/5 border border-line rounded-xl text-foreground text-sm font-medium placeholder:text-muted-foreground dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-transparent disabled:opacity-50 transition-all'}
                placeholder="Name on the card"
            />
            {cardholderName && !isValidPersonName(cardholderName) && (
                <p className="text-rose-500 dark:text-rose-400 text-xs mt-2 font-medium">Please enter a valid name (letters only).</p>
            )}
        </div>
        <div>
            <label className={compact
                ? 'block text-xs font-semibold text-foreground mb-1.5'
                : 'block text-xs font-bold text-muted-foreground mb-2 uppercase tracking-wide'}>
                Card details
            </label>
            <div className={`w-full bg-white dark:bg-white/5 border rounded-xl transition-all ${compact
                ? 'px-3.5 py-3 border-line focus-within:border-plum/40 focus-within:ring-1 focus-within:ring-ring/40'
                : 'px-4 py-3 border-line focus-within:ring-2 focus-within:ring-ring/40 focus-within:border-transparent'} ${processing ? 'opacity-50 pointer-events-none' : ''}`}>
                <CardElement
                    options={{ ...cardOptions, disabled: processing }}
                    onChange={handleCardChange}
                />
            </div>
            {cardError && (
                <p className="text-rose-500 dark:text-rose-400 text-xs mt-2 font-medium flex items-center gap-1">
                    <Info className="w-3 h-3" /> {cardError}
                </p>
            )}
        </div>
    </div>
    );
};

interface AddressFieldsProps {
    addressLine1: string;
    setAddressLine1: (v: string) => void;
    city: string;
    setCity: (v: string) => void;
    state: string;
    setState: (v: string) => void;
    billingCountry: string;
    setBillingCountry: (v: string) => void;
    postalCode: string;
    setPostalCode: (v: string) => void;
    countries: { code: string; name: string }[];
    processing: boolean;
    compact?: boolean;
}

/** Billing address — line1/city/state/country/postal code — collected up front so Stripe Tax
 * (VAT) can be resolved before card details are ever shown. */
export const HoursAddressFields: React.FC<AddressFieldsProps> = ({
    addressLine1, setAddressLine1, city, setCity, state, setState,
    billingCountry, setBillingCountry, postalCode, setPostalCode, countries,
    processing, compact,
}) => {
    const labelClass = compact
        ? 'block text-xs font-semibold text-foreground mb-1.5'
        : 'block text-xs font-bold text-muted-foreground mb-2 uppercase tracking-wide';
    const inputClass = compact
        ? 'w-full px-3.5 py-2.5 bg-white dark:bg-white/5 border border-line rounded-xl text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-plum/40 focus:ring-1 focus:ring-ring/40 disabled:opacity-50'
        : 'w-full px-4 py-3 bg-white dark:bg-white/5 border border-line rounded-xl text-foreground text-sm font-medium placeholder:text-muted-foreground dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-transparent disabled:opacity-50 transition-all';

    return (
        <div className={compact ? 'space-y-3' : 'space-y-4'}>
            <div>
                <label className={labelClass}>Address line 1</label>
                <input
                    type="text"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    disabled={processing}
                    className={inputClass}
                    placeholder="123 Main St"
                />
                {addressLine1 && !isValidAddressLine(addressLine1) && (
                    <p className="text-rose-500 dark:text-rose-400 text-xs mt-2 font-medium">Please enter a valid address.</p>
                )}
            </div>
            <div className={compact ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-2 gap-4'}>
                <div>
                    <label className={labelClass}>City</label>
                    <input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        disabled={processing}
                        className={inputClass}
                        placeholder="Dublin"
                    />
                    {city && !isValidPlaceName(city) && (
                        <p className="text-rose-500 dark:text-rose-400 text-xs mt-2 font-medium">Please enter a valid city.</p>
                    )}
                </div>
                <div>
                    <label className={labelClass}>State / Province</label>
                    <input
                        type="text"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        disabled={processing}
                        className={inputClass}
                        placeholder="Optional"
                    />
                </div>
            </div>
            <div className={compact ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-2 gap-4'}>
                <div>
                    <label className={labelClass}>Country</label>
                    <select
                        value={billingCountry}
                        onChange={(e) => setBillingCountry(e.target.value)}
                        disabled={processing}
                        className={inputClass}
                    >
                        {countries.map((c) => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                    </select>
                </div>
                {requiresPostalCode(billingCountry) && (
                    <div>
                        <label className={labelClass}>Postal code</label>
                        <input
                            type="text"
                            value={postalCode}
                            onChange={(e) => setPostalCode(e.target.value)}
                            disabled={processing}
                            placeholder={getPostalCodePlaceholder(billingCountry)}
                            className={inputClass}
                        />
                        {postalCode && !validatePostalCode(postalCode, billingCountry) && (
                            <p className="text-rose-500 dark:text-rose-400 text-xs mt-2 font-medium">Please enter a valid postal code.</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
